<template>
  <div class="mobile-notes-page">
    <header class="mobile-notes-header">
      <h1 class="mobile-notes-large-title">{{ $t("memos.title") }}</h1>
    </header>

    <div class="mobile-notes-composer-wrap">
      <div class="mobile-notes-composer" :class="{ focused: composerFocused, filled: !!draft.trim() }">
        <div class="mobile-notes-field" :class="{ composing: composerComposing }">
          <div
            class="mobile-notes-highlight"
            aria-hidden="true"
            v-html="highlightMarkup(draft)"
          ></div>
          <textarea
            ref="composer"
            v-model="draft"
            class="mobile-notes-textarea"
            rows="1"
            :placeholder="$t('memos.placeholder')"
            :disabled="saving"
            spellcheck="false"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            data-form-type="other"
            data-lpignore="true"
            data-1p-ignore="true"
            data-bwignore="true"
            @focus="onComposerFocus"
            @blur="onComposerBlur"
            @input="onComposerInput"
            @click="refreshTagSuggest('composer')"
            @keyup="onComposerKeyup"
            @scroll="syncComposerScroll"
            @keydown="onComposerKeydown"
            @compositionstart="composerComposing = true"
            @compositionend="onComposerCompositionEnd"
          ></textarea>
        </div>
        <button
          type="button"
          class="mobile-notes-mode-btn mobile-notes-mode-btn--composer"
          :title="$t('memos.focus')"
          :aria-label="$t('memos.focus')"
          @mousedown.prevent
          @click="openFocusFromComposer"
        >
          <i class="bi-arrows-fullscreen" aria-hidden="true"></i>
        </button>
        <button
          type="button"
          class="mobile-notes-send"
          :disabled="saving || !draft.trim()"
          :aria-label="$t('memos.save')"
          @mousedown.prevent
          @click="onCreate"
        >
          <i class="bi-arrow-up" aria-hidden="true"></i>
        </button>
      </div>
      <ul
        v-if="showComposerSuggest"
        class="mobile-notes-suggest"
        role="listbox"
        :aria-label="$t('memos.tagSuggest')"
      >
        <li v-for="(tag, idx) in tagSuggestions" :key="tag">
          <button
            type="button"
            role="option"
            :class="{ active: idx === suggestIndex }"
            :aria-selected="idx === suggestIndex ? 'true' : 'false'"
            @mousedown.prevent="applyTagSuggest(tag, 'composer')"
          >
            <span class="mobile-notes-suggest-hash">#</span>{{ tag }}
          </button>
        </li>
      </ul>
      <p class="mobile-notes-composer-hint">{{ $t("memos.sendHint") }}</p>
    </div>

    <div class="mobile-notes-filters">
      <div class="mobile-notes-date-filter">
        <button
          type="button"
          class="mobile-notes-chip"
          :class="{ active: !activeDay }"
          @click="clearDay"
        >
          {{ $t("memos.allDates") }}
        </button>
        <button
          type="button"
          class="mobile-notes-date-label"
          :class="{ active: !!activeDay }"
          @click="openDayPicker"
        >
          <i class="bi-calendar3" aria-hidden="true"></i>
          <span class="mobile-notes-date-text">{{ activeDayLabel }}</span>
        </button>
        <input
          ref="dayInput"
          class="mobile-notes-date-input"
          type="date"
          :value="activeDay || ''"
          :max="todayDay"
          tabindex="-1"
          aria-hidden="true"
          @change="onDayInput"
          @input="onDayInput"
        />
        <button
          v-if="activeDay"
          type="button"
          class="mobile-notes-chip"
          @click="clearDay"
        >
          {{ $t("memos.clearDate") }}
        </button>
      </div>
      <div
        v-if="tags.length"
        class="mobile-notes-tags"
        role="list"
      >
        <button
          type="button"
          class="mobile-notes-chip"
          :class="{ active: !activeTag }"
          role="listitem"
          @click="clearTag"
        >
          {{ $t("memos.all") }}
        </button>
        <button
          v-for="entry in visibleTags"
          :key="entry.tag"
          type="button"
          class="mobile-notes-chip"
          :class="{ active: activeTag === entry.tag }"
          role="listitem"
          @click="toggleTag(entry.tag)"
        >
          #{{ entry.tag }}
          <span v-if="entry.count" class="mobile-notes-chip-count">{{ entry.count }}</span>
        </button>
        <button
          v-if="tagsOverflow"
          type="button"
          class="mobile-notes-chip mobile-notes-tags-more"
          role="listitem"
          :aria-expanded="tagsExpanded ? 'true' : 'false'"
          @click="tagsExpanded = !tagsExpanded"
        >
          {{ tagsExpanded ? $t("memos.tagsCollapse") : $t("memos.tagsExpand") }}
        </button>
      </div>
    </div>

    <div ref="timeline" class="mobile-notes-timeline" @scroll="onScroll">
      <p v-if="loading && !items.length" class="mobile-notes-status">…</p>
      <div v-else-if="!items.length" class="mobile-notes-empty">
        <i class="bi-journal-text mobile-notes-empty-icon" aria-hidden="true"></i>
        <p>{{ $t("memos.empty") }}</p>
        <p class="mobile-notes-empty-sub">{{ $t("memos.emptyHint") }}</p>
      </div>

      <article
        v-for="note in items"
        :key="note.id"
        class="mobile-notes-card"
        :class="{ editing: editingId === note.id, 'menu-open': menuId === note.id }"
      >
        <template v-if="editingId === note.id">
          <div class="mobile-notes-field mobile-notes-field-edit" :class="{ composing: editComposing }">
            <div
              class="mobile-notes-highlight"
              aria-hidden="true"
              v-html="highlightMarkup(editDraft)"
            ></div>
            <textarea
              ref="editArea"
              v-model="editDraft"
              class="mobile-notes-textarea mobile-notes-edit-textarea"
              rows="3"
              spellcheck="false"
              autocomplete="off"
              autocorrect="off"
              autocapitalize="off"
              data-form-type="other"
              data-lpignore="true"
              data-1p-ignore="true"
              data-bwignore="true"
              @input="onEditInput"
              @click="refreshTagSuggest('edit')"
              @keyup="onEditKeyup"
              @scroll="syncEditScroll"
              @keydown="onEditKeydown($event, note)"
              @compositionstart="editComposing = true"
              @compositionend="onEditCompositionEnd"
            ></textarea>
            <ul
              v-if="showEditSuggest"
              class="mobile-notes-suggest mobile-notes-suggest-edit"
              role="listbox"
              :aria-label="$t('memos.tagSuggest')"
            >
              <li v-for="(tag, idx) in tagSuggestions" :key="tag">
                <button
                  type="button"
                  role="option"
                  :class="{ active: idx === suggestIndex }"
                  :aria-selected="idx === suggestIndex ? 'true' : 'false'"
                  @mousedown.prevent="applyTagSuggest(tag, 'edit')"
                >
                  <span class="mobile-notes-suggest-hash">#</span>{{ tag }}
                </button>
              </li>
            </ul>
          </div>
          <div class="mobile-notes-edit-bar">
            <button type="button" class="mobile-notes-text-btn muted" :disabled="saving" @click="cancelEdit">
              {{ $t("todoDetails.cancel") }}
            </button>
            <button
              type="button"
              class="mobile-notes-mode-btn"
              :disabled="saving"
              :title="$t('memos.focus')"
              :aria-label="$t('memos.focus')"
              @click="openFocusFromEdit(note)"
            >
              <i class="bi-arrows-fullscreen" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              class="mobile-notes-text-btn primary"
              :disabled="saving || !editDraft.trim()"
              @click="onSaveEdit(note)"
            >
              {{ $t("memos.save") }}
            </button>
          </div>
        </template>
        <template v-else>
          <div
            class="mobile-notes-body markdown-body"
            autocomplete="off"
            data-form-type="other"
            data-lpignore="true"
            data-1p-ignore="true"
            data-bwignore="true"
            v-html="renderBody(note.body)"
            @click="onBodyClick"
          ></div>
          <div class="mobile-notes-card-meta">
            <time :datetime="note.createdAt || note.updatedAt">{{
              formatTime(note.createdAt || note.updatedAt)
            }}</time>
            <span v-if="note.todoId" class="mobile-notes-linked">{{ $t("activity.linkedTask") }}</span>
            <div class="mobile-notes-card-trail">
              <!-- PC: inline icon actions (no popup overlap) -->
              <div class="mobile-notes-actions-inline" role="group">
                <button
                  type="button"
                  class="mobile-notes-icon-btn"
                  :title="$t('memos.edit')"
                  :aria-label="$t('memos.edit')"
                  @click="startEdit(note)"
                >
                  <i class="bi-pencil" aria-hidden="true"></i>
                </button>
                <button
                  type="button"
                  class="mobile-notes-icon-btn danger"
                  :title="$t('memos.delete')"
                  :aria-label="$t('memos.delete')"
                  @click="onRemove(note)"
                >
                  <i class="bi-trash" aria-hidden="true"></i>
                </button>
              </div>
              <!-- Phone: ··· menu -->
              <div v-if="menuId === note.id" class="mobile-notes-menu" role="menu">
                <button type="button" role="menuitem" @click="startEdit(note)">
                  <i class="bi-pencil" aria-hidden="true"></i>
                  {{ $t("memos.edit") }}
                </button>
                <button type="button" role="menuitem" class="danger" @click="onRemove(note)">
                  <i class="bi-trash" aria-hidden="true"></i>
                  {{ $t("memos.delete") }}
                </button>
              </div>
              <button
                type="button"
                class="mobile-notes-more"
                :aria-expanded="menuId === note.id ? 'true' : 'false'"
                :aria-label="$t('ui.more')"
                @click.stop="toggleMenu(note.id)"
              >
                <i class="bi-three-dots" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </template>
      </article>
      <p v-if="loading && items.length" class="mobile-notes-status">…</p>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";
const { createNotesMarkdown } = require("../helpers/createNotesMarkdown.js");
const { neutralizeCredentialPairs } = require("../helpers/notesDisplaySafe.js");
const {
  openNotesFocus,
  setNotesFocusPendingBody,
} = require("./notesNav.js");

const TAGS_VISIBLE_LIMIT = 10;
const { isSystemActivityTag } = require("../helpers/todoActivityLog.js");
const { completeTagSuggestion } = require("./noteTagSuggest.js");

function tagPattern() {
  return /#([\p{L}\p{N}_-]{1,64})/gu;
}

export default {
  name: "MobileNotesPage",
  data() {
    return {
      md: createNotesMarkdown(),
      draft: "",
      editDraft: "",
      editingId: null,
      menuId: null,
      saving: false,
      loadMoreLock: false,
      composerFocused: false,
      composerComposing: false,
      editComposing: false,
      suggestTarget: null,
      suggestQuery: null,
      suggestStart: -1,
      suggestIndex: 0,
      tagsExpanded: false,
    };
  },
  computed: {
    ...mapGetters([
      "notesItems",
      "notesTags",
      "notesNextCursor",
      "notesActiveTag",
      "notesActiveDay",
      "notesLoading",
      "config",
    ]),
    isFocusDefault() {
      return this.config && this.config.notesEditorMode === "focus";
    },
    items() {
      return this.notesItems || [];
    },
    tags() {
      return this.notesTags || [];
    },
    /** Non-system tags for the primary chip row (budget TAGS_VISIBLE_LIMIT). */
    primaryTags() {
      return this.tags
        .filter((entry) => !isSystemActivityTag(entry.tag))
        .slice(0, TAGS_VISIBLE_LIMIT);
    },
    /** System activity tags always + non-system beyond the primary budget. */
    overflowTags() {
      const system = this.tags.filter((entry) => isSystemActivityTag(entry.tag));
      const rest = this.tags
        .filter((entry) => !isSystemActivityTag(entry.tag))
        .slice(TAGS_VISIBLE_LIMIT);
      return system.concat(rest);
    },
    tagsOverflow() {
      return this.overflowTags.length > 0;
    },
    visibleTags() {
      if (this.tagsExpanded) return this.primaryTags.concat(this.overflowTags);
      let head = this.primaryTags;
      if (!this.activeTag) return head;
      if (head.some((entry) => entry.tag === this.activeTag)) return head;
      const active = this.tags.find((entry) => entry.tag === this.activeTag);
      if (!active) return head;
      if (head.length >= TAGS_VISIBLE_LIMIT) {
        head = head.slice(0, TAGS_VISIBLE_LIMIT - 1);
      }
      return head.concat(active);
    },
    activeTag() {
      return this.notesActiveTag;
    },
    activeDay() {
      return this.notesActiveDay;
    },
    todayDay() {
      return moment().format("YYYY-MM-DD");
    },
    activeDayLabel() {
      if (!this.activeDay) return this.$t("memos.pickDate");
      const zh = String(this.$i18n && this.$i18n.locale ? this.$i18n.locale : "").startsWith("zh");
      const m = moment(this.activeDay, "YYYY-MM-DD");
      if (!m.isValid()) return this.activeDay;
      return zh ? m.format("M月D日") : m.format("MMM D");
    },
    loading() {
      return this.notesLoading;
    },
    tagSuggestions() {
      if (this.suggestQuery == null) return [];
      const q = this.suggestQuery;
      const names = (this.tags || []).map((t) => t.tag).filter(Boolean);
      const matched = names.filter((name) => name.startsWith(q)).slice(0, 8);
      return matched;
    },
    showComposerSuggest() {
      return (
        this.suggestTarget === "composer" &&
        !this.composerComposing &&
        this.tagSuggestions.length > 0
      );
    },
    showEditSuggest() {
      return this.suggestTarget === "edit" && !this.editComposing && this.tagSuggestions.length > 0;
    },
  },
  mounted() {
    this.reload();
    this.$nextTick(() => this.autoGrowComposer());
    document.addEventListener("click", this.onDocClick);
  },
  activated() {
    this.reload();
  },
  beforeUnmount() {
    document.removeEventListener("click", this.onDocClick);
  },
  methods: {
    async reload() {
      this.menuId = null;
      try {
        await this.$store.dispatch("notesLoadInitial");
      } catch (_) {
        /* empty timeline */
      }
    },
    escapeHtml(text) {
      return String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    /** Live #tag highlight for composer / edit textarea (mirror layer). */
    highlightMarkup(text) {
      const raw = neutralizeCredentialPairs(text == null ? "" : String(text));
      if (!raw) return "";
      const escaped = this.escapeHtml(raw);
      const withTags = escaped.replace(tagPattern(), '<span class="mobile-notes-htag-live">#$1</span>');
      // Keep trailing newline visible so height matches textarea
      return withTags.replace(/\n$/g, "\n&nbsp;").replace(/\n/g, "<br>");
    },
    onComposerFocus() {
      this.composerFocused = true;
      if (this.isFocusDefault) {
        this.openFocusFromComposer();
      }
    },
    onComposerBlur() {
      this.composerFocused = false;
      this.composerComposing = false;
      // Delay so suggestion mousedown can fire first
      setTimeout(() => {
        if (this.suggestTarget === "composer") this.clearTagSuggest();
      }, 120);
    },
    openFocusFromComposer() {
      const body = this.draft || "";
      if (body) setNotesFocusPendingBody(body);
      this.draft = "";
      this.clearTagSuggest();
      this.composerFocused = false;
      openNotesFocus({ noteId: null });
      this.$nextTick(() => this.autoGrowComposer());
    },
    openFocusFromEdit(note) {
      const body = this.editDraft || (note && note.body) || "";
      const id = note && note.id;
      this.cancelEdit();
      if (!id) {
        if (body) setNotesFocusPendingBody(body);
        openNotesFocus({ noteId: null });
        return;
      }
      // Prefer server body via load; if light edit had unsaved text, push as pending only for new
      if (body && body !== (note.body || "")) {
        setNotesFocusPendingBody(body);
      }
      openNotesFocus({ noteId: id });
    },
    onComposerCompositionEnd() {
      this.composerComposing = false;
      this.onComposerInput();
    },
    onEditCompositionEnd() {
      this.editComposing = false;
      this.onEditInput();
    },
    onComposerInput() {
      this.autoGrowComposer();
      this.$nextTick(() => {
        this.syncComposerScroll();
        this.refreshTagSuggest("composer");
      });
    },
    onEditInput() {
      this.autoGrowEdit();
      this.$nextTick(() => {
        this.syncEditScroll();
        this.refreshTagSuggest("edit");
      });
    },
    onComposerKeyup(event) {
      if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
        this.refreshTagSuggest("composer");
      }
    },
    onEditKeyup(event) {
      if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
        this.refreshTagSuggest("edit");
      }
    },
    getTagQueryAtCursor(text, cursor) {
      const before = String(text || "").slice(0, cursor);
      const m = before.match(/#([\p{L}\p{N}_-]*)$/u);
      if (!m) return null;
      return { start: m.index, query: m[1] };
    },
    refreshTagSuggest(target) {
      const ta =
        target === "composer"
          ? this.$refs.composer
          : (() => {
              const el = this.$refs.editArea;
              return Array.isArray(el) ? el[0] : el;
            })();
      if (!ta) {
        this.clearTagSuggest();
        return;
      }
      if ((target === "composer" && this.composerComposing) || (target === "edit" && this.editComposing)) {
        this.clearTagSuggest();
        return;
      }
      const text = target === "composer" ? this.draft : this.editDraft;
      const found = this.getTagQueryAtCursor(text, ta.selectionStart || 0);
      if (!found) {
        this.clearTagSuggest();
        return;
      }
      this.suggestTarget = target;
      this.suggestQuery = found.query;
      this.suggestStart = found.start;
      this.suggestIndex = 0;
    },
    clearTagSuggest() {
      this.suggestTarget = null;
      this.suggestQuery = null;
      this.suggestStart = -1;
      this.suggestIndex = 0;
    },
    applyTagSuggest(tag, target) {
      const ta =
        target === "composer"
          ? this.$refs.composer
          : (() => {
              const el = this.$refs.editArea;
              return Array.isArray(el) ? el[0] : el;
            })();
      if (!ta || this.suggestStart < 0) return;
      const text = target === "composer" ? this.draft : this.editDraft;
      const cursor = ta.selectionStart || 0;
      const start = this.suggestStart;
      const { text: next, caret } = completeTagSuggestion(text, start, cursor, tag);
      this.clearTagSuggest();
      // DOM-first so trailing "\n" + caret survive the v-model round-trip
      ta.focus();
      if (typeof ta.setRangeText === "function") {
        ta.setRangeText(`#${tag}\n`, start, cursor, "end");
        const domValue = ta.value;
        if (target === "composer") this.draft = domValue;
        else this.editDraft = domValue;
      } else {
        if (target === "composer") this.draft = next;
        else this.editDraft = next;
        ta.value = next;
      }
      this.$nextTick(() => {
        const pos = Math.min(caret, (ta.value || "").length);
        ta.focus();
        ta.setSelectionRange(pos, pos);
        if (target === "composer") this.onComposerInput();
        else this.onEditInput();
        // Vue may reset selection after binding the model
        requestAnimationFrame(() => {
          ta.setSelectionRange(pos, pos);
        });
      });
    },
    syncComposerScroll() {
      const ta = this.$refs.composer;
      const field = ta && ta.parentElement;
      const hl = field && field.querySelector(".mobile-notes-highlight");
      if (!ta || !hl) return;
      hl.scrollTop = ta.scrollTop;
      hl.scrollLeft = ta.scrollLeft;
    },
    syncEditScroll() {
      const el = this.$refs.editArea;
      const ta = Array.isArray(el) ? el[0] : el;
      const field = ta && ta.parentElement;
      const hl = field && field.querySelector(".mobile-notes-highlight");
      if (!ta || !hl) return;
      hl.scrollTop = ta.scrollTop;
      hl.scrollLeft = ta.scrollLeft;
    },
    linkifyTagsInHtml(html) {
      return html.replace(/(^|>)([^<]*)/g, (full, prefix, text) => {
        const linked = text.replace(tagPattern(), (_, tag) => {
          const safe = this.escapeHtml(tag);
          return `<button type="button" class="mobile-notes-htag" data-tag="${safe}">#${safe}</button>`;
        });
        return prefix + linked;
      });
    },
    renderBody(body) {
      const html = this.md.render(neutralizeCredentialPairs(body || ""));
      return this.linkifyTagsInHtml(html);
    },
    formatTime(iso) {
      if (!iso) return "";
      const m = moment(iso);
      if (!m.isValid()) return iso;
      const now = moment();
      const zh = String(this.$i18n && this.$i18n.locale ? this.$i18n.locale : "").startsWith("zh");
      if (now.diff(m, "seconds") < 60) return this.$t("memos.justNow");
      if (now.isSame(m, "day")) return m.format("HH:mm");
      if (now.clone().subtract(1, "day").isSame(m, "day")) {
        return `${this.$t("memos.yesterday")} ${m.format("HH:mm")}`;
      }
      if (zh) {
        if (now.isSame(m, "year")) return m.format("M月D日 HH:mm");
        return m.format("YYYY年M月D日 HH:mm");
      }
      if (now.isSame(m, "year")) return m.format("MMM D, HH:mm");
      return m.format("MMM D, YYYY HH:mm");
    },
    autoGrowComposer() {
      const el = this.$refs.composer;
      if (!el) return;
      el.style.height = "auto";
      const h = `${Math.min(el.scrollHeight, 160)}px`;
      el.style.height = h;
      const hl = el.parentElement && el.parentElement.querySelector(".mobile-notes-highlight");
      if (hl) hl.style.height = h;
    },
    autoGrowEdit() {
      const el = this.$refs.editArea;
      const node = Array.isArray(el) ? el[0] : el;
      if (!node) return;
      node.style.height = "auto";
      const h = `${Math.min(node.scrollHeight, 220)}px`;
      node.style.height = h;
      const hl = node.parentElement && node.parentElement.querySelector(".mobile-notes-highlight");
      if (hl) hl.style.height = h;
    },
    handleSuggestKeydown(event, target) {
      const open =
        target === "composer" ? this.showComposerSuggest : this.showEditSuggest;
      if (!open) return false;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        this.suggestIndex = (this.suggestIndex + 1) % this.tagSuggestions.length;
        return true;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        this.suggestIndex =
          (this.suggestIndex - 1 + this.tagSuggestions.length) % this.tagSuggestions.length;
        return true;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        if (!(event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          const tag = this.tagSuggestions[this.suggestIndex];
          if (tag) this.applyTagSuggest(tag, target);
          return true;
        }
      }
      if (event.key === "Escape") {
        event.preventDefault();
        this.clearTagSuggest();
        return true;
      }
      return false;
    },
    onComposerKeydown(event) {
      if (this.handleSuggestKeydown(event, "composer")) return;
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
        event.preventDefault();
        this.onCreate();
      }
    },
    onEditKeydown(event, note) {
      if (this.handleSuggestKeydown(event, "edit")) return;
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
        event.preventDefault();
        this.onSaveEdit(note);
      }
      if (event.key === "Escape") {
        event.preventDefault();
        this.cancelEdit();
      }
    },
    clearTag() {
      if (!this.activeTag) return;
      this.$store.dispatch("notesSetTagFilter", null);
    },
    toggleTag(tag) {
      const next = this.activeTag === tag ? null : tag;
      this.menuId = null;
      this.$store.dispatch("notesSetTagFilter", next);
    },
    clearDay() {
      if (!this.activeDay) return;
      this.$store.dispatch("notesSetDayFilter", null);
    },
    openDayPicker() {
      const el = this.$refs.dayInput;
      if (!el) return;
      try {
        if (typeof el.showPicker === "function") {
          el.showPicker();
          return;
        }
      } catch (e) {
        /* 部分 WebView 需落到 input.click */
      }
      el.focus();
      el.click();
    },
    onDayInput(event) {
      const value = event.target && event.target.value ? event.target.value : "";
      this.$store.dispatch("notesSetDayFilter", value || null);
    },
    onBodyClick(event) {
      const btn = event.target.closest(".mobile-notes-htag");
      if (!btn) return;
      event.preventDefault();
      const tag = btn.getAttribute("data-tag");
      if (tag) this.toggleTag(tag);
    },
    toggleMenu(id) {
      this.menuId = this.menuId === id ? null : id;
    },
    onDocClick(event) {
      if (this.menuId && !event.target.closest(".mobile-notes-card-trail")) {
        this.menuId = null;
      }
      if (this.suggestTarget && !event.target.closest(".mobile-notes-suggest") && !event.target.closest(".mobile-notes-textarea")) {
        this.clearTagSuggest();
      }
    },
    async onCreate() {
      const body = (this.draft || "").trim();
      if (!body || this.saving) return;
      this.saving = true;
      this.menuId = null;
      this.clearTagSuggest();
      try {
        await this.$store.dispatch("notesCreate", { body });
        this.draft = "";
        this.$nextTick(() => this.autoGrowComposer());
      } catch (_) {
        /* keep draft */
      } finally {
        this.saving = false;
      }
    },
    startEdit(note) {
      this.menuId = null;
      this.clearTagSuggest();
      if (this.isFocusDefault) {
        openNotesFocus({ noteId: note.id });
        return;
      }
      this.editingId = note.id;
      this.editDraft = note.body || "";
      this.$nextTick(() => {
        this.autoGrowEdit();
        const el = this.$refs.editArea;
        const node = Array.isArray(el) ? el[0] : el;
        if (node) node.focus();
      });
    },
    cancelEdit() {
      this.editingId = null;
      this.editDraft = "";
      this.clearTagSuggest();
      this.editComposing = false;
    },
    async onSaveEdit(note) {
      const body = (this.editDraft || "").trim();
      if (!body || this.saving) return;
      this.saving = true;
      try {
        await this.$store.dispatch("notesUpdate", {
          id: note.id,
          body,
          baseRevision: note.revision,
        });
        this.cancelEdit();
      } catch (err) {
        if (err && err.status === 409) this.cancelEdit();
      } finally {
        this.saving = false;
      }
    },
    async onRemove(note) {
      this.menuId = null;
      if (!window.confirm(this.$t("memos.deleteConfirm"))) return;
      try {
        await this.$store.dispatch("notesRemove", { id: note.id });
        if (this.editingId === note.id) this.cancelEdit();
      } catch (_) {
        /* keep */
      }
    },
    async onScroll() {
      const el = this.$refs.timeline;
      if (!el || this.loading || this.loadMoreLock || !this.notesNextCursor) return;
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (remaining > 120) return;
      this.loadMoreLock = true;
      try {
        await this.$store.dispatch("notesLoadMore");
      } catch (_) {
        /* ignore */
      } finally {
        this.loadMoreLock = false;
      }
    },
  },
};
</script>
